import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  LayoutDashboard, 
  Users, 
  ShoppingCart, 
  Truck,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Database,
  Tag,
  Package,
  Image,
  CreditCard,
  Settings,
  UserCheck,
  Highlighter,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";

const sidebarItems = [
  { name: "Dashboard", path: "/admin/dashboard", icon: LayoutDashboard },
  { 
    name: "Masters", 
    icon: Database,
    children: [
      { name: "Categories", path: "/admin/masters/categories", icon: Tag },
      { name: "Products", path: "/admin/masters/products", icon: Package },
      { name: "Banners", path: "/admin/masters/banners", icon: Image },
      { name: "Payment Methods", path: "/admin/masters/payment-methods", icon: CreditCard },
      { name: "Highlight", path: "/admin/masters/highlight", icon: Highlighter },
    ]
  },
  { name: "Stores", path: "/admin/stores", icon: Truck },
  { name: "Orders", path: "/admin/orders", icon: ShoppingCart },
  { name: "Users", path: "/admin/users", icon: Users },
  { name: "Delivery Managers", path: "/admin/delivery-managers", icon: UserCheck },
  { name: "Settings", path: "/admin/settings", icon: Settings },
];

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mastersOpen, setMastersOpen] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("ecomdemo_user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    // Auto-expand Masters if on a masters page
    if (location.pathname.includes("/admin/masters")) {
      setMastersOpen(true);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem("ecomdemo_token");
    localStorage.removeItem("ecomdemo_user");
    toast.success("Logged out successfully");
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#FBFAF8] flex admin-app">
      {/* Desktop Sidebar */}
      <aside 
        className={`hidden md:flex flex-col admin-sidebar ${sidebarOpen ? 'w-64' : 'w-20'} transition-all duration-300 relative`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <span className="admin-brand-mark"><Sparkles size={20} /></span>
            {sidebarOpen && (
              <span className="admin-brand-name">LitSpark<span>.</span></span>
            )}
          </div>
        </div>

        {/* Toggle Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute top-4 -right-3 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-900 shadow-sm"
          data-testid="sidebar-toggle"
        >
          <ChevronRight size={14} className={`transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {sidebarItems.map((item) => (
            item.children ? (
              <div key={item.name}>
                <button
                  onClick={() => setMastersOpen(!mastersOpen)}
                  className={`sidebar-item w-full justify-between ${location.pathname.includes("/admin/masters") ? 'text-[#6C42F5]' : ''}`}
                  data-testid={`sidebar-${item.name.toLowerCase()}`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon size={20} />
                    {sidebarOpen && <span>{item.name}</span>}
                  </div>
                  {sidebarOpen && (
                    <ChevronDown 
                      size={16} 
                      className={`transition-transform ${mastersOpen ? 'rotate-180' : ''}`} 
                    />
                  )}
                </button>
                {mastersOpen && sidebarOpen && (
                  <div className="ml-4 mt-1 space-y-1 border-l border-gray-200 pl-3">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        className={({ isActive }) => 
                          `sidebar-item text-sm ${isActive ? 'active' : ''}`
                        }
                        data-testid={`sidebar-${child.name.toLowerCase()}`}
                      >
                        <child.icon size={18} />
                        <span>{child.name}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => 
                  `sidebar-item ${isActive ? 'active' : ''}`
                }
                data-testid={`sidebar-${item.name.toLowerCase()}`}
              >
                <item.icon size={20} />
                {sidebarOpen && <span>{item.name}</span>}
              </NavLink>
            )
          ))}
        </nav>

        {/* User & Logout */}
        <div className="p-4 border-t border-gray-200">
          {sidebarOpen && user && (
            <div className="mb-4 px-3">
              <p className="text-gray-900 font-medium truncate">{user.name}</p>
              <p className="text-gray-500 text-sm truncate">{user.email}</p>
            </div>
          )}
          <Button
            onClick={handleLogout}
            variant="ghost"
            className={`w-full justify-start gap-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100 ${!sidebarOpen && 'justify-center'}`}
            data-testid="logout-btn"
          >
            <LogOut size={20} />
            {sidebarOpen && <span>Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <span className="admin-brand-mark admin-brand-mark-small"><Sparkles size={16} /></span>
            <span className="admin-brand-name">LitSpark<span>.</span></span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-gray-900"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="bg-white border-b border-gray-200 p-4">
            <nav className="space-y-1">
              {sidebarItems.map((item) => (
                item.children ? (
                  <div key={item.name}>
                    <button
                      onClick={() => setMastersOpen(!mastersOpen)}
                      className={`sidebar-item w-full justify-between ${location.pathname.includes("/admin/masters") ? 'text-[#6C42F5]' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon size={20} />
                        <span>{item.name}</span>
                      </div>
                      <ChevronDown 
                        size={16} 
                        className={`transition-transform ${mastersOpen ? 'rotate-180' : ''}`} 
                      />
                    </button>
                    {mastersOpen && (
                      <div className="ml-4 mt-1 space-y-1 border-l border-gray-200 pl-3">
                        {item.children.map((child) => (
                          <NavLink
                            key={child.path}
                            to={child.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={({ isActive }) => 
                              `sidebar-item text-sm ${isActive ? 'active' : ''}`
                            }
                          >
                            <child.icon size={18} />
                            <span>{child.name}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) => 
                      `sidebar-item ${isActive ? 'active' : ''}`
                    }
                  >
                    <item.icon size={20} />
                    <span>{item.name}</span>
                  </NavLink>
                )
              ))}
            </nav>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="w-full justify-start gap-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                <LogOut size={20} />
                <span>Logout</span>
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 md:ml-0 pt-16 md:pt-0">
        <div className="p-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
